import React, { useState, useEffect, useMemo } from 'react';
import { 
  Input, 
  Select, 
  Button, 
  Space, 
  Drawer, 
  Typography, 
  Row, 
  Col,
  Badge,
  Tag
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined, 
  SortAscendingOutlined,
  CloseOutlined
} from '@ant-design/icons';
import './FilterSortBar.css';

const { Option } = Select;
const { Text } = Typography;

export interface SortOption {
  value: string;
  label: string;
}

export interface FilterSortBarProps {
  // Search
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearch?: () => void;
  
  // Category filter
  categoryOptions?: string[];
  categoryValue?: string;
  onCategoryChange?: (value: string | undefined) => void;
  
  // Sort
  sortOptions?: SortOption[];
  sortValue?: string;
  onSortChange?: (value: string | undefined) => void;
  
  // Advanced filters
  showAdvancedFilters?: boolean;
  onAdvancedFiltersOpen?: () => void;
  advancedFilterCount?: number;
  
  // Reset
  onReset?: () => void;
  hasActiveFilters?: boolean;
  
  // Custom filters (chips)
  activeFilters?: Array<{ key: string; label: string; onRemove: () => void }>;
  
  // Mobile responsive
  mobileBreakpoint?: 'sm' | 'md' | 'lg';
}

const FilterSortBar: React.FC<FilterSortBarProps> = ({
  searchPlaceholder = 'Ara...',
  searchValue,
  onSearchChange,
  onSearch,
  categoryOptions = [],
  categoryValue,
  onCategoryChange,
  sortOptions = [],
  sortValue,
  onSortChange,
  showAdvancedFilters = false,
  onAdvancedFiltersOpen,
  advancedFilterCount = 0,
  onReset,
  hasActiveFilters = false,
  activeFilters = [],
  mobileBreakpoint = 'md'
}) => {
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(
        (mobileBreakpoint === 'sm' && width < 576) ||
        (mobileBreakpoint === 'md' && width < 768) ||
        (mobileBreakpoint === 'lg' && width < 992)
      );
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [mobileBreakpoint]);

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch();
    }
  };

  const handleSortChange = (value: string | null) => {
    if (onSortChange) {
      onSortChange(value || undefined);
    }
  };

  const handleCategoryChange = (value: string) => {
    if (onCategoryChange) {
      onCategoryChange(value || undefined);
    }
  };

  // Mobile Drawer Content
  const mobileContent = (
    <div className="filter-sort-mobile">
      <div className="filter-sort-mobile-section">
        <Text strong>Arama</Text>
        <Input
          placeholder={searchPlaceholder}
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => onSearchChange?.(e.target.value)}
          onPressEnter={handleSearchKeyPress}
          size="large"
          style={{ marginTop: '8px' }}
        />
      </div>

      {categoryOptions.length > 0 && (
        <div className="filter-sort-mobile-section">
          <Text strong>Kategori</Text>
          <Select
            placeholder="Kategori seçin"
            value={categoryValue}
            onChange={handleCategoryChange}
            allowClear
            showSearch
            style={{ width: '100%', marginTop: '8px' }}
            size="large"
          >
            {categoryOptions.map(opt => (
              <Option key={opt} value={opt}>{opt}</Option>
            ))}
          </Select>
        </div>
      )}

      {sortOptions.length > 0 && (
        <div className="filter-sort-mobile-section">
          <Text strong>Sırala</Text>
            <Select
              placeholder="Sırala"
              value={sortValue}
              onChange={(val) => handleSortChange(val)}
              allowClear
              style={{ width: '100%', marginTop: '8px' }}
              size="large"
            >
              {sortOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
        </div>
      )}

      {activeFilters.length > 0 && (
        <div className="filter-sort-mobile-section">
          <Text strong>Aktif Filtreler</Text>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {activeFilters.map(filter => (
              <Tag
                key={filter.key}
                closable
                onClose={filter.onRemove}
                color="blue"
              >
                {filter.label}
              </Tag>
            ))}
          </div>
        </div>
      )}

      <div className="filter-sort-mobile-actions">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {showAdvancedFilters && (
            <Button
              type="default"
              icon={<FilterOutlined />}
              onClick={() => {
                setMobileDrawerVisible(false);
                onAdvancedFiltersOpen?.();
              }}
              block
              size="large"
            >
              Gelişmiş Filtreler
              {advancedFilterCount > 0 && (
                <Badge count={advancedFilterCount} offset={[8, 0]} />
              )}
            </Button>
          )}
          
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => {
              onSearch?.();
              setMobileDrawerVisible(false);
            }}
            block
            size="large"
          >
            Ara
          </Button>
          
          {hasActiveFilters && onReset && (
            <Button
              onClick={() => {
                onReset();
                setMobileDrawerVisible(false);
              }}
              block
              size="large"
            >
              Sıfırla
            </Button>
          )}
        </Space>
      </div>
    </div>
  );

  // Desktop Content
  const desktopContent = (
    <div className="filter-sort-desktop">
      <Row gutter={[12, 12]} align="middle">
        {/* Search */}
        <Col xs={24} sm={24} md={8} lg={6}>
          <Input
            placeholder={searchPlaceholder}
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onPressEnter={handleSearchKeyPress}
            size="large"
            className="filter-sort-input"
          />
        </Col>

        {/* Category */}
        {categoryOptions.length > 0 && (
          <Col xs={24} sm={12} md={6} lg={5}>
            <Select
              placeholder="Kategori"
              value={categoryValue}
              onChange={handleCategoryChange}
              allowClear
              showSearch
              style={{ width: '100%' }}
              size="large"
            >
              {categoryOptions.map(opt => (
                <Option key={opt} value={opt}>{opt}</Option>
              ))}
            </Select>
          </Col>
        )}

        {/* Sort */}
        {sortOptions.length > 0 && (
          <Col xs={24} sm={12} md={5} lg={4}>
            <Select
              placeholder="Sırala"
              value={sortValue}
              onChange={(val) => handleSortChange(val)}
              allowClear
              style={{ width: '100%' }}
              size="large"
              suffixIcon={<SortAscendingOutlined />}
            >
              {sortOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Col>
        )}

        {/* Actions */}
        <Col xs={24} sm={24} md={5} lg={9}>
          <Space wrap style={{ width: '100%', justifyContent: isMobile ? 'center' : 'flex-end' }}>
            {showAdvancedFilters && (
              <Button
                icon={<FilterOutlined />}
                onClick={onAdvancedFiltersOpen}
                size="large"
              >
                Filtreler
                {advancedFilterCount > 0 && (
                  <Badge count={advancedFilterCount} offset={[4, -2]} />
                )}
              </Button>
            )}
            
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={onSearch}
              size="large"
            >
              Ara
            </Button>
            
            {hasActiveFilters && onReset && (
              <Button
                onClick={onReset}
                size="large"
                icon={<CloseOutlined />}
              >
                Sıfırla
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Active Filters Chips */}
      {activeFilters.length > 0 && (
        <div className="filter-sort-active-filters" style={{ marginTop: '12px' }}>
          <Space wrap size={[8, 8]}>
            {activeFilters.map(filter => (
              <Tag
                key={filter.key}
                closable
                onClose={filter.onRemove}
                color="blue"
                style={{ margin: 0 }}
              >
                {filter.label}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="filter-sort-mobile-trigger">
          <Button
            type="default"
            icon={<FilterOutlined />}
            onClick={() => setMobileDrawerVisible(true)}
            block
            size="large"
            style={{ marginBottom: '12px' }}
          >
            Filtrele & Sırala
            {(hasActiveFilters || advancedFilterCount > 0) && (
              <Badge count={activeFilters.length + advancedFilterCount} offset={[8, 0]} />
            )}
          </Button>
        </div>

        <Drawer
          title="Filtrele & Sırala"
          placement="bottom"
          height="auto"
          open={mobileDrawerVisible}
          onClose={() => setMobileDrawerVisible(false)}
          className="filter-sort-drawer"
        >
          {mobileContent}
        </Drawer>
      </>
    );
  }

  return <div className="filter-sort-bar">{desktopContent}</div>;
};

export default FilterSortBar;
